
import { useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { CertificateMetadata, StudentCertificationProgress, CertificationCriteria, CertificationLevel, StudentRequirementProgress } from '@/types/certification';

// Mock data for certification progress
const getMockProgressData = (studentId: number, programTitle: string | null, programCategory: string | null): StudentCertificationProgress => {
  return {
    studentId,
    programTitle: programTitle || '',
    programCategory: programCategory || '',
    overallProgress: Math.floor(Math.random() * 100),
    requirementProgress: [],
    currentLevel: Math.random() > 0.5 ? 'merit' : 'pass',
    certificationStatus: Math.random() > 0.7 ? 'issued' : 'eligible',
    prerequisites: { met: true },
    updatedAt: new Date().toISOString()
  };
};

// Mock criteria for certifications
const getMockCriteria = (): CertificationCriteria[] => {
  return [
    {
      programTitle: 'DIT Complete Package',
      programCategory: 'DIT Programs',
      requirements: [
        { 
          id: '1', 
          type: 'attendance', 
          description: 'Minimum class attendance', 
          minimumValue: 75, 
          maxValue: 100, 
          optional: false,
          weight: 30
        },
        { 
          id: '2', 
          type: 'assessment', 
          description: 'Course assessments', 
          minimumValue: 70, 
          maxValue: 100, 
          optional: false,
          weight: 40
        },
        { 
          id: '3', 
          type: 'project', 
          description: 'Final project', 
          minimumValue: 65, 
          maxValue: 100, 
          optional: false,
          weight: 30
        }
      ],
      passingThreshold: 65,
      meritThreshold: 75,
      distinctionThreshold: 85
    },
    {
      programTitle: 'File Three',
      programCategory: 'English File',
      requirements: [
        { 
          id: '1', 
          type: 'attendance', 
          description: 'Minimum class attendance', 
          minimumValue: 80, 
          maxValue: 100, 
          optional: false,
          weight: 20
        },
        { 
          id: '2', 
          type: 'assessment', 
          description: 'Written exams', 
          minimumValue: 70, 
          maxValue: 100, 
          optional: false,
          weight: 40
        },
        { 
          id: '3', 
          type: 'assessment', 
          description: 'Speaking assessment', 
          minimumValue: 65, 
          maxValue: 100, 
          optional: false,
          weight: 40
        }
      ],
      passingThreshold: 65,
      meritThreshold: 75,
      distinctionThreshold: 90
    }
  ];
};

// Mock certificates
const mockCertificates: CertificateMetadata[] = [
  {
    id: '1',
    studentId: 1,
    programTitle: 'DIT Complete Package',
    programCategory: 'DIT Programs',
    certificationLevel: 'merit',
    issueDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString(),
    issuedBy: 'John Instructor',
    verificationCode: 'CERT-1234-5678',
    certificateNumber: 'DIT/2023/001',
    status: 'issued'
  }
];

export const useCertification = ({
  studentId,
  programTitle,
  programCategory,
}: {
  studentId: number;
  programTitle: string | null;
  programCategory: string | null;
}) => {
  const [certificates, setCertificates] = useState<CertificateMetadata[]>(
    mockCertificates.filter(cert => cert.studentId === studentId)
  );
  const [progress, setProgress] = useState<StudentCertificationProgress | null>(null);
  const [criteria, setCriteria] = useState<CertificationCriteria | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load progress data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));
        setProgress(getMockProgressData(studentId, programTitle, programCategory));
        
        // Find appropriate criteria based on program title and category
        const allCriteria = getMockCriteria();
        const matchedCriteria = allCriteria.find(c => 
          c.programTitle === programTitle && c.programCategory === programCategory
        ) || allCriteria[0]; // Default to first criteria if no match
        
        setCriteria(matchedCriteria);
        setError(null);
      } catch (err) {
        console.error('Error fetching certification data:', err);
        setError('Failed to load certification data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [studentId, programTitle, programCategory]);

  // Function to verify prerequisites
  const verifyPrerequisites = useCallback((
    studentId: number,
    programTitle: string,
    programCategory: string
  ) => {
    // For demo, always return prerequisites as met
    return { met: true };
  }, []);

  // Function to update requirement progress
  const updateProgress = useCallback((
    studentId: number,
    programTitle: string,
    programCategory: string,
    requirementId: string,
    achievedValue: number,
    evidence?: string,
    verifiedBy?: string
  ): void => {
    if (!progress) return;
    
    const updatedProgress = { ...progress };
    const requirementProgress = updatedProgress.requirementProgress.find(
      rp => rp.requirementId === requirementId
    );
    
    if (requirementProgress) {
      requirementProgress.achieved = achievedValue;
      requirementProgress.completed = true;
      requirementProgress.verifiedBy = verifiedBy;
      requirementProgress.verifiedAt = new Date().toISOString();
    } else {
      const newRequirementProgress: StudentRequirementProgress = {
        requirementId,
        achieved: achievedValue,
        completed: true,
        verifiedBy,
        verifiedAt: new Date().toISOString(),
        evidence
      };
      updatedProgress.requirementProgress.push(newRequirementProgress);
    }
    
    // Update overall progress
    updatedProgress.overallProgress = Math.min(
      100,
      updatedProgress.requirementProgress.reduce(
        (sum, rp) => sum + (rp.achieved || 0), 
        0
      ) / updatedProgress.requirementProgress.length
    );
    
    setProgress(updatedProgress);
    
    toast.success('Requirement progress updated');
  }, [progress]);

  // Function to issue a certificate
  const issueProgramCertificate = useCallback((
    studentId: number,
    programTitle: string,
    programCategory: string,
    issuedBy: string
  ): CertificateMetadata | null => {
    try {
      const certLevel: CertificationLevel = 'merit';
      const newCertificate: CertificateMetadata = {
        id: uuidv4(),
        studentId,
        programTitle,
        programCategory,
        certificationLevel: certLevel,
        issueDate: new Date().toISOString(),
        expiryDate: new Date(new Date().setFullYear(new Date().getFullYear() + 2)).toISOString(),
        issuedBy,
        verificationCode: `CERT-${Math.floor(Math.random() * 10000)}-${Math.floor(Math.random() * 10000)}`,
        certificateNumber: `${programCategory.substring(0,3).toUpperCase()}/${new Date().getFullYear()}/${Math.floor(Math.random() * 1000)}`,
        status: 'issued'
      };

      setCertificates(prev => [...prev, newCertificate]);
      
      // Update progress to show certificate is issued
      setProgress(prev => prev ? {
        ...prev,
        certificationStatus: 'issued'
      } : null);

      return newCertificate;
    } catch (err) {
      console.error('Error issuing certificate:', err);
      return null;
    }
  }, []);

  return {
    certificates,
    progress,
    criteria,
    isLoading,
    error,
    issueProgramCertificate,
    verifyPrerequisites,
    updateProgress
  };
};
